import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getUserToken } from "../context/UserAuthContext";

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO = "https://customer-assets.emergentagent.com/job_261f6229-25c6-462a-bf7c-d1c15ef074eb/artifacts/rv1gzzra_logo%20v2%20xpro.png";

const OBJECTIVES = [
  { value: "salud_general",       label: "Salud general", desc: "Mantenerme activo y saludable" },
  { value: "perder_grasa",        label: "Perder grasa", desc: "Reducir porcentaje graso" },
  { value: "ganar_musculo",       label: "Ganar músculo", desc: "Aumentar masa muscular" },
  { value: "ganar_fuerza",        label: "Ganar fuerza", desc: "Mejorar mi rendimiento" },
  { value: "resistencia",         label: "Resistencia", desc: "Aguantar más tiempo" },
  { value: "recuperacion_lesion", label: "Recuperación", desc: "Volver tras una lesión" },
];

const EXPERIENCE = [
  { value: "nunca",     label: "Nunca he entrenado",     desc: "Empiezo desde cero" },
  { value: "ocasional", label: "Alguna vez",              desc: "He entrenado pero con poca regularidad" },
  { value: "habitual",  label: "Entrenamiento habitual", desc: "Llevo tiempo entrenando" },
];

const SUPERVISION = [
  { value: "por_mi_cuenta",       label: "Por mi cuenta" },
  { value: "programa_preparado",  label: "Con programa preparado" },
  { value: "con_entrenador",      label: "Con entrenador personal" },
  { value: "indiferente",         label: "Me da igual" },
];

const DAYS = [1, 2, 3, 4, 5];

export default function OnboardingPersonalizationPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [genero, setGenero] = useState("");
  const [objetivo, setObjetivo] = useState("");
  const [experiencia, setExperiencia] = useState("");
  const [dias, setDias] = useState(null);
  const [supervision, setSupervision] = useState("");
  const [pesoKg, setPesoKg] = useState("");
  const [alturaCm, setAlturaCm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const canSubmit = genero && objetivo && experiencia && dias && supervision && pesoKg && alturaCm;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/onboarding/personalizacion`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getUserToken()}` },
        body: JSON.stringify({
          genero,
          objetivo_principal: objetivo,
          experiencia_previa: experiencia,
          dias_semana_deseados: dias,
          preferencia_supervision: supervision,
          peso_kg: parseFloat(pesoKg),
          altura_cm: parseFloat(alturaCm),
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || "Error al guardar"); return; }
      navigate("/inicio", { replace: true });
    } catch {
      setError("Error de conexión. Inténtalo de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="sticky top-0 z-10 bg-black/95 backdrop-blur border-b border-white/8">
        <div className="max-w-lg mx-auto px-4 h-14 flex items-center justify-between">
          <img src={LOGO} alt="XPRO FITNESS" className="h-7 w-auto" />
          <span className="text-[10px] tracking-[0.25em] uppercase text-white/40">Paso 2 de 2</span>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-8 pb-16">
        <div className="mb-8">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#F2C230] font-black mb-2">Tu perfil</p>
          <h1 className="text-3xl font-black">¿Cuál es tu objetivo?</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8" data-testid="personalization-form">
          {/* Género */}
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/50 mb-3">1. Género</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: "hombre", label: "Hombre" },
                { value: "mujer",  label: "Mujer"  },
              ].map((g) => (
                <button key={g.value} type="button" data-testid={`genero-${g.value}`}
                  onClick={() => setGenero(g.value)}
                  className={`py-4 font-black text-sm border transition-colors ${genero === g.value ? "border-[#F2C230] bg-[#F2C230]/10 text-[#F2C230]" : "border-white/10 text-white/60 hover:border-white/25"}`}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>

          {/* Objective */}
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/50 mb-3">2. Objetivo principal</p>
            <div className="grid grid-cols-2 gap-2">
              {OBJECTIVES.map((o) => (
                <button key={o.value} type="button" data-testid={`objetivo-${o.value}`}
                  onClick={() => setObjetivo(o.value)}
                  className={`p-4 text-left border transition-colors ${objetivo === o.value ? "border-[#F2C230] bg-[#F2C230]/10" : "border-white/10 hover:border-white/25"}`}
                >
                  <div className="font-black text-sm">{o.label}</div>
                  <div className="text-xs text-white/40 mt-0.5">{o.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Experience */}
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/50 mb-3">3. Experiencia previa</p>
            <div className="space-y-2">
              {EXPERIENCE.map((ex) => (
                <button key={ex.value} type="button" data-testid={`exp-${ex.value}`}
                  onClick={() => setExperiencia(ex.value)}
                  className={`w-full p-4 text-left border transition-colors flex items-center justify-between ${experiencia === ex.value ? "border-[#F2C230] bg-[#F2C230]/10" : "border-white/10 hover:border-white/25"}`}
                >
                  <div>
                    <div className="font-black text-sm">{ex.label}</div>
                    <div className="text-xs text-white/40">{ex.desc}</div>
                  </div>
                  {experiencia === ex.value && <span className="w-2 h-2 rounded-full bg-[#F2C230]" />}
                </button>
              ))}
            </div>
          </div>

          {/* Days */}
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/50 mb-3">4. Días por semana disponibles</p>
            <div className="flex gap-2">
              {DAYS.map((d) => (
                <button key={d} type="button" data-testid={`dias-${d}`}
                  onClick={() => setDias(d)}
                  className={`flex-1 py-4 font-black text-lg border transition-colors ${dias === d ? "border-[#F2C230] bg-[#F2C230]/10 text-[#F2C230]" : "border-white/10 text-white/60 hover:border-white/25"}`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          {/* Supervision */}
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/50 mb-3">5. Preferencia de supervisión</p>
            <div className="grid grid-cols-2 gap-2">
              {SUPERVISION.map((s) => (
                <button key={s.value} type="button" data-testid={`supervision-${s.value}`}
                  onClick={() => setSupervision(s.value)}
                  className={`py-3 px-4 font-bold text-sm border transition-colors ${supervision === s.value ? "border-[#F2C230] bg-[#F2C230]/10 text-[#F2C230]" : "border-white/10 text-white/60 hover:border-white/25"}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Peso y Altura */}
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/50 mb-3">6. Medidas corporales</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] tracking-[0.2em] uppercase font-bold text-white/40 mb-2">Peso (kg)</label>
                <input
                  type="number"
                  min="20"
                  max="300"
                  step="0.1"
                  value={pesoKg}
                  onChange={(e) => setPesoKg(e.target.value)}
                  placeholder="70.5"
                  required
                  data-testid="input-peso-kg"
                  className="w-full bg-black border border-white/10 text-white placeholder:text-white/20 px-4 py-3 text-sm outline-none focus:border-[#F2C230] transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.2em] uppercase font-bold text-white/40 mb-2">Altura (cm)</label>
                <input
                  type="number"
                  min="100"
                  max="250"
                  step="0.1"
                  value={alturaCm}
                  onChange={(e) => setAlturaCm(e.target.value)}
                  placeholder="175"
                  required
                  data-testid="input-altura-cm"
                  className="w-full bg-black border border-white/10 text-white placeholder:text-white/20 px-4 py-3 text-sm outline-none focus:border-[#F2C230] transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
            </div>
          </div>

          {error && <p className="text-red-400 text-sm" data-testid="personalization-error">{error}</p>}

          <button type="submit" disabled={!canSubmit || submitting} data-testid="personalization-submit"
            className="w-full bg-[#F2C230] text-black font-black text-[11px] tracking-[0.25em] uppercase py-4 hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? "Generando rutina..." : "Ver mi rutina"}
          </button>
        </form>
      </div>
    </div>
  );
}
